/**
 * COMPONENT: BedFieldInput
 * Rendu d'UN champ du bulletin, dans le style du renderer public existant
 * (mêmes classes, mêmes composants : PhoneInputIframe, RadixSelect,
 * SchoolAutocomplete), mais piloté par le modèle du wizard plutôt que par
 * react-hook-form : le brouillon serveur est la source de vérité.
 */

import { PhoneInputIframe, RadixSelect, SchoolAutocomplete } from '../ui';
import type { RadixSelectOption } from '../ui';
import {
  BED_CHECKBOX_FALSE,
  BED_CHECKBOX_TRUE,
  isChecked,
  isNotStudent,
  STUDENT_FALSE_VALUE,
  STUDENT_TRUE_VALUE,
  type BedValues,
} from '../../lib/bed/wizard-model';
import type { FormFieldWithValues } from '../../types/form';
import { COUNTRIES } from '../../types/school';

interface BedFieldInputProps {
  field: FormFieldWithValues;
  values: BedValues;
  required: boolean;
  error?: string | null;
  disabled?: boolean;
  onChange: (fieldId: string, value: string) => void;
  /** Rendu spécifique de l'école (autocomplete + création) — cf. BedSchoolField. */
  renderSchool?: (field: FormFieldWithValues) => React.ReactNode;
}

/** Les codes pays sont stockés en ISO3, affichés en clair. */
function optionLabel(option: string, contactFieldMap?: string): string {
  if (contactFieldMap === 'country' || contactFieldMap === undefined) {
    const country = COUNTRIES.find((c) => c.code === option);
    if (country) return country.label;
  }
  return option;
}

const INPUT_CLASS =
  'w-full h-10 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900';

export function BedFieldInput({
  field,
  values,
  required,
  error,
  disabled = false,
  onChange,
  renderSchool,
}: BedFieldInputProps) {
  const value = values[field.id] ?? '';

  // « Je ne suis plus étudiant(e) » : le template est un select en base, mais
  // le participant voit une case à cocher — et la valeur STOCKÉE reste
  // 'Jeune Actif' / 'Étudiant' pour rester compatible avec le trigger SQL
  // `create_contact_from_submission`. Même contrat que le renderer BPI.
  if (field.contact_field_map === 'is_not_student') {
    return (
      <div className="flex items-start gap-2 py-1">
        <input
          type="checkbox"
          id={field.id}
          checked={isNotStudent(values, field.id)}
          disabled={disabled}
          onChange={(event) =>
            onChange(field.id, event.target.checked ? STUDENT_TRUE_VALUE : STUDENT_FALSE_VALUE)
          }
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
        />
        <label htmlFor={field.id} className="cursor-pointer text-sm text-zinc-700">
          Je ne suis plus étudiant(e)
        </label>
      </div>
    );
  }

  // Case à cocher : rendue inline (label à droite), pas de titre au-dessus.
  if (field.field_type === 'checkbox') {
    return (
      <div className="flex items-start gap-2 py-1">
        <input
          type="checkbox"
          id={field.id}
          checked={isChecked(values, field.id)}
          disabled={disabled}
          onChange={(event) =>
            onChange(field.id, event.target.checked ? BED_CHECKBOX_TRUE : BED_CHECKBOX_FALSE)
          }
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
        />
        <label htmlFor={field.id} className="cursor-pointer text-sm text-zinc-700">
          {field.label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label htmlFor={field.id} className="block text-sm font-medium text-zinc-900">
        {field.label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {field.contact_field_map === 'school' && renderSchool ? (
        renderSchool(field)
      ) : field.contact_field_map === 'school' ? (
        <SchoolAutocomplete
          id={field.id}
          value={value}
          onChange={(next) => onChange(field.id, next)}
          placeholder={field.placeholder || 'Tapez le nom de votre école…'}
          hasError={Boolean(error)}
        />
      ) : field.field_type === 'select' && field.options ? (
        <RadixSelect
          value={value}
          onChange={(next) => onChange(field.id, next)}
          options={field.options.map(
            (option): RadixSelectOption => ({
              value: option,
              label: optionLabel(option, field.contact_field_map),
            })
          )}
          placeholder="Sélectionnez une option"
          disablePortal={true}
        />
      ) : field.field_type === 'phone' ? (
        <PhoneInputIframe
          id={field.id}
          value={value}
          onChange={(next) => onChange(field.id, next ?? '')}
          placeholder={field.placeholder || '6 12 34 56 78'}
        />
      ) : field.field_type === 'textarea' ? (
        <textarea
          id={field.id}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(field.id, event.target.value)}
          placeholder={field.placeholder || ''}
          className="min-h-[96px] w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
        />
      ) : (
        <input
          id={field.id}
          type={
            field.field_type === 'email'
              ? 'email'
              : field.field_type === 'number'
                ? 'number'
                : field.field_type === 'date'
                  ? 'date'
                  : 'text'
          }
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(field.id, event.target.value)}
          placeholder={field.placeholder || ''}
          className={`${INPUT_CLASS} ${error ? 'border-red-300' : ''}`}
        />
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
